/**
 * metricTreeApi — Customer Portal client for /metric-trees endpoints (Metric Tree).
 * Mirrors Boards/api.js: REACT_APP_API_URL base + x-api-key + x-access-token
 * headers (utils/DefaultFunction.js getApiHeaders). NOT the src/api/client.js
 * wrapper — that prepends `/api` which RRS doesn't use for portal routes.
 */
import axios from 'axios';
import { getApiHeaders } from '../../utils/DefaultFunction';

const BASE = process.env.REACT_APP_API_URL || '';

const request = async (method, path, opts = {}) => {
  const url = `${BASE}${path}`;
  const r = await axios({
    method,
    url,
    data: opts.data,
    params: opts.params,
    headers: getApiHeaders(),
    timeout: opts.timeout || 130000,
  });
  return r.data;
};

export const metricTreeApi = {
  /** List all metric trees for the current tenant. */
  list() {
    return request('GET', '/metric-trees');
  },

  /** Fetch a single tree with nodes + edges embedded. */
  get(id) {
    return request('GET', `/metric-trees/${id}`);
  },

  /** Create a new tree. body: { name, nodes, edges, default_time_frame?, layout? } */
  create(payload) {
    return request('POST', '/metric-trees', { data: payload });
  },

  /** Update a tree. body: { name?, nodes?, edges?, layout? } */
  update(id, payload) {
    return request('PUT', `/metric-trees/${id}`, { data: payload });
  },

  /** Delete a tree. */
  remove(id) {
    return request('DELETE', `/metric-trees/${id}`);
  },

  /**
   * Compute node values + edge correlations against live ClickHouse data.
   * @returns {Promise<{success, data:{ nodes:[{node_id,value}], edges:[{edge_id,corr,n}], time_frame, cache_hit }}>}
   */
  compute(id, opts = {}) {
    const body = {};
    if (opts.time_frame) body.time_frame = opts.time_frame;
    return request('POST', `/metric-trees/${id}/compute`, { data: body });
  },

  // MT-08: compute an UNSAVED graph — the in-memory nodes/edges are sent in the
  // body (no id), so a just-configured node gets a value at runtime without a
  // save-then-reload cycle. { nodes, edges, time_frame, cohort_scope? }.
  preview(payload = {}) {
    return request('POST', '/metric-trees/preview', { data: payload });
  },

  // ── Lookups for the node-config pickers (same endpoints Boards TileEditor uses) ──

  // POST /fetch-unique-events → { data: { events: [{display_name, name}, ...] } }
  async listEvents() {
    try {
      const r = await request('POST', '/fetch-unique-events', { data: {}, timeout: 20000 });
      const arr = r?.data?.events || [];
      if (!Array.isArray(arr) || arr.length === 0) {
        console.warn('[metric-tree] listEvents: no events returned', { shape: r && Object.keys(r) });
      }
      return { data: Array.isArray(arr) ? arr : [] };
    } catch (e) {
      console.error('[metric-tree] listEvents failed:', e?.response?.status, e?.response?.data || e?.message);
      return { data: [], error: e?.response?.status || e?.message };
    }
  },

  /**
   * Upload a Media-node image to the same tenant media bucket the Email Designer
   * uses (POST /media-upload, multipart). Returns the hosted URL so the image
   * survives reload and isn't a throwaway blob:/data URI (BUG-MT-08).
   * @returns {Promise<string>} hosted image URL
   */
  async uploadImage(file) {
    const form = new FormData();
    form.append('file', file);
    const r = await request('POST', '/media-upload', {
      data: form,
      timeout: 60000,
    });
    if (r && r.success === false) throw new Error(r.message || 'Upload failed');
    const url = r?.fileUrl || r?.url || r?.data?.url;
    if (!url) throw new Error('Upload succeeded but no URL was returned');
    return url;
  },

  // MT-01: tenant user directory for the share picker. GET /user/list →
  // { data: [{ id, username, email, name, ... }] }. Normalized to a plain
  // array of { id, name, email, username } so the picker stays shape-safe
  // (identical contract to Boards' listUsers).
  async listUsers() {
    try {
      const r = await request('GET', '/user/list');
      const arr = Array.isArray(r?.data) ? r.data : [];
      return {
        data: arr
          .filter((u) => u && u.id !== undefined && u.id !== null)
          .map((u) => ({
            id: String(u.id),
            name: u.name || u.full_name || u.username || u.email || `User ${u.id}`,
            email: u.email || '',
            username: u.username || '',
          })),
      };
    } catch (e) {
      return { data: [], error: e?.response?.status || e?.message };
    }
  },

  // POST /fetch-unique-attributes {event} → { data: { event, attributes: [{name,type,...}, ...] } }
  async listEventAttributes(event) {
    if (!event) return { data: [] };
    try {
      const r = await request('POST', '/fetch-unique-attributes', { data: { event } });
      const arr = r?.data?.attributes || [];
      return { data: Array.isArray(arr) ? arr : [] };
    } catch (e) {
      return { data: [] };
    }
  },
};

export const TIME_FRAMES = [
  { value: 'all_time', label: 'All time' },
  { value: 'last_24_hours', label: 'Last 24 hours' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'last_year', label: 'Last year' },
];

export const AGG_OPTIONS = [
  { value: 'count', label: 'Count' },
  { value: 'unique_contacts', label: 'Unique customers' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
];

// Canvas node categories (palette).
export const NODE_TYPE_OPTIONS = [
  { value: 'metric', label: 'Metric' },
  { value: 'strategy', label: 'Strategy' },
  { value: 'note', label: 'Note' },
  { value: 'text', label: 'Text' },
  { value: 'media', label: 'Media' },
];

export const NOTE_COLORS = ['#FEF3C7', '#DCFCE7', 'var(--brand-tint-strong)', '#FCE7F3', '#F1F5F9'];

// A metric node is a raw METRIC (event aggregate) or a FORMULA (derived).
export const NODE_KIND_OPTIONS = [
  { value: 'metric', label: 'Metric' },
  { value: 'formula', label: 'Formula (derived)' },
];

/** Default node.data for a freshly-added node of a given palette type. */
export function newNodeData(type) {
  switch (type) {
    case 'strategy':
      return { label: 'New objective', type: 'strategy', annotation: { objective: 'New objective', target: '' } };
    case 'note':
      return { label: 'Note', type: 'note', annotation: { text: 'New note', color: NOTE_COLORS[0] } };
    case 'text':
      return { label: 'Text', type: 'text', annotation: { content: 'Text', size: 16 } };
    case 'media':
      return { label: 'Media', type: 'media', annotation: { url: '', caption: '' } };
    default:
      return {
        label: 'New metric', type: 'metric', kind: 'metric',
        metric: { event: '', agg: 'count' }, formula: { operator: '+', inputs: [] },
      };
  }
}

// Formula operators. Stored as ASCII (+ - * /); rendered with the Mixpanel glyphs.
export const OPERATORS = ['+', '-', '*', '/'];
export const OP_LABEL = { '+': '+', '-': '−', '*': '×', '/': '÷' };
export const OPERATOR_OPTIONS = [
  { value: '+', label: 'Add  ( + )' },
  { value: '-', label: 'Subtract  ( − )' },
  { value: '*', label: 'Multiply  ( × )' },
  { value: '/', label: 'Divide  ( ÷ )' },
];

/** Aggs that require a numeric property. */
export const AGG_NEEDS_PROPERTY = new Set(['sum', 'avg']);

/** Unit word shown under a metric card's value (Mixpanel-style). */
export const unitLabel = (data) => {
  if (data?.kind === 'formula') return '';
  const agg = data?.metric?.agg;
  if (agg === 'count') return 'events';
  if (agg === 'unique_contacts') return 'customers';
  if (agg === 'sum') return 'total';
  if (agg === 'avg') return 'average';
  return '';
};

/** 2–3 uppercase initials for an owner avatar. MT-03: a one-word name falls
 *  back to its first TWO characters (never a single character). */
export const initials = (name) => {
  if (!name) return '';
  const parts = String(name).trim().split(/[\s@._-]+/).filter(Boolean);
  const out = parts.length >= 2
    ? parts.slice(0, 3).map((p) => p[0]).join('')       // first char of up to 3 tokens
    : (parts[0] || String(name)).slice(0, 2);           // single token → first two chars
  return out.toUpperCase().slice(0, 3);
};

/** Human label for an agg value (for the node summary line). */
export const aggLabel = (agg) =>
  (AGG_OPTIONS.find((o) => o.value === agg) || {}).label || agg || 'count';

/** Format a computed metric value compactly (1_234_567 → "1.2M"). */
export const formatMetricValue = (v) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (Math.abs(n) >= 1000) {
    return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
  }
  // Small numbers: show up to 2 decimals but trim trailing zeros.
  return new Intl.NumberFormat('en', { maximumFractionDigits: 2 }).format(n);
};
